import { cx } from '@linaria/core';
import { useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { PostloadedIframe } from '@pm2/react-utils';
import { useModalContentManager } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { Tooltip } from '@ui/kit';
import { CrossIcon } from '@ui/kit/icons';
import { formatTimestamp } from '@utils/common';
import { configRefs } from '@utils/constants';
import { CSSRefTransition, Chevron, HideFor } from '@utils/react';
import { modal, section, transitionDurationMs, header, heading, contentWrapper, navButton, chevronWrapper, infoTextWrapper, infoText, date, closeButtonWrapper, closeButton, iframe, transitionGroup, modifiers } from './modal.preview.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

const makePreviewModalConfig = props => ({
  className: modal,
  includeSpeechButton: false,
  content: /*#__PURE__*/jsx(PreviewModal, {
    ...props
  })
});
function PreviewModal(props) {
  const [transitionDirection, setDirection] = useState();
  const [activeEntryIndex, setActiveEntryIndex] = useState(props.initialIndex);
  const modal = useModalContentManager();
  const user = useUserIdentityState(state => state.user);
  const currentEntry = props.entries[activeEntryIndex];
  if (!currentEntry) {
    return null;
  }
  const src = (() => {
    const params = new URLSearchParams();
    params.append("componentID", encodeURIComponent(currentEntry.component.id));
    if (!props.startFromScratch) {
      params.append("componentEntryID", encodeURIComponent(currentEntry.component.entryId));
    }
    params.append("componentType", encodeURIComponent(currentEntry.component.type));
    if (user) {
      params.append("userID", user.id);
    }
    if (user?.institution) {
      params.append("institutionID", user.institution.id);
    }
    return `${configRefs.apis.componentFramework.dangerouslyAssertSyncValue()}/Proxy/${"student" in currentEntry ? "ComponentEntry" : "ComponentViewer"}?${params.toString()}`;
  })();
  const hasPrevious = activeEntryIndex > 0;
  const hasNext = activeEntryIndex < props.entries.length - 1;
  const handleGoToPrevious = () => {
    setDirection(modifiers.backwards.className);
    setActiveEntryIndex(activeEntryIndex - 1);
  };
  const handleGoToNext = () => {
    setDirection(modifiers.forwards.className);
    setActiveEntryIndex(activeEntryIndex + 1);
  };
  return /*#__PURE__*/jsx(TransitionGroup, {
    className: cx(transitionGroup, transitionDirection),
    children: /*#__PURE__*/jsx(CSSRefTransition, {
      timeout: transitionDurationMs,
      classNames: section,
      children: ref => /*#__PURE__*/jsxs("section", {
        ref: ref,
        className: section,
        children: [/*#__PURE__*/jsxs("header", {
          className: header,
          children: [/*#__PURE__*/jsx("div", {
            children: /*#__PURE__*/jsx("h1", {
              className: heading,
              children: currentEntry.component.title
            })
          }), !props.disableHeaderCenterPanel && /*#__PURE__*/jsxs("div", {
            className: contentWrapper,
            children: [props.entries.length > 1 && /*#__PURE__*/jsxs("button", {
              "aria-label": "Se forrige",
              "aria-disabled": !hasPrevious,
              disabled: !hasPrevious,
              className: navButton,
              onClick: handleGoToPrevious
              // Ensure that focus remains within the iframe when
              // changin between entries
              // eslint-disable-next-line jsx-a11y/no-autofocus
              ,
              autoFocus: !hasNext,
              children: [/*#__PURE__*/jsx("div", {
                className: cx("left", chevronWrapper),
                children: /*#__PURE__*/jsx(Chevron, {
                  size: 8,
                  inversed: true
                })
              }), /*#__PURE__*/jsx(HideFor, {
                breakpoint: breakpoints.mobile,
                children: "Se forrige"
              })]
            }), /*#__PURE__*/jsxs("div", {
              className: infoTextWrapper,
              children: ["student" in currentEntry && /*#__PURE__*/jsxs(Fragment, {
                children: [/*#__PURE__*/jsx("span", {
                  className: cx(infoText, "studentName"),
                  children: currentEntry.student.name
                }), /*#__PURE__*/jsx(HideFor, {
                  breakpoint: breakpoints.mobile,
                  className: infoText,
                  children: currentEntry.student.team
                })]
              }), /*#__PURE__*/jsx("div", {
                className: cx(infoText, date, user?.role === "student" && "student"),
                children: formatTimestamp(currentEntry.modifiedAt ?? currentEntry.createdAt)
              })]
            }), props.entries.length > 1 && /*#__PURE__*/jsxs("button", {
              "aria-label": "Se n\xE6ste",
              "aria-disabled": !hasNext,
              disabled: !hasNext,
              className: navButton,
              onClick: handleGoToNext
              // Ensure that focus remains within the iframe when
              // changin between entries
              // eslint-disable-next-line jsx-a11y/no-autofocus
              ,
              autoFocus: hasNext,
              children: [/*#__PURE__*/jsx(HideFor, {
                breakpoint: breakpoints.mobile,
                children: "Se n\xE6ste"
              }), /*#__PURE__*/jsx("div", {
                className: cx("right", chevronWrapper),
                children: /*#__PURE__*/jsx(Chevron, {
                  size: 8
                })
              })]
            })]
          }), /*#__PURE__*/jsx("div", {
            className: closeButtonWrapper,
            children: /*#__PURE__*/jsx(Tooltip, {
              placement: "bottom",
              ariaType: "label",
              body: "Luk besvarelse",
              children: ref => /*#__PURE__*/jsx("button", {
                ref: ref,
                onClick: () => modal.onDismiss(),
                className: closeButton,
                children: /*#__PURE__*/jsx(CrossIcon, {
                  height: 16
                })
              })
            })
          })]
        }), /*#__PURE__*/jsx(PostloadedIframe, {
          className: iframe,
          title: currentEntry.component.title,
          src: window.gProxy.forwardTo(src)
        })]
      })
    }, activeEntryIndex)
  });
}

export { makePreviewModalConfig };
//# sourceMappingURL=modal.preview.js.map
